/**
 * Event Type Detection Utility
 *
 * Centralized utility for detecting and categorizing HMC event types.
 * Provides consistent type detection across all components:
 * - EventReportViewer (table view)
 * - GuestEventsTable (guest selection)
 * - Event filters
 * - Email templates
 *
 * Uses a 2-tier detection strategy:
 * 1. Priority 1: Explicit event_type field (if not 'general')
 * 2. Priority 2: Text-based detection from event content (for 'general' events)
 *
 * @module eventTypeDetector
 */

import type { HMCEvent, EventTypeExtended, MusicGenre } from '../hmcEventsStore';

/**
 * Result of event type detection
 */
export interface EventTypeResult {
  /** The detected event type */
  type: EventTypeExtended;
  /** Human-readable label for display */
  label: string;
  /** CSS class name for custom styling (hmc-type-*) */
  cssClass: string;
  /** Tailwind CSS classes for badge styling */
  tailwindClasses: string;
}

/**
 * Simplified filter categories for event filtering
 */
export type EventFilterCategory = 'concerts' | 'sports' | 'others';

/**
 * Configuration for each event type's styling
 */
interface TypeStyleConfig {
  label: string;
  cssClass: string;
  tailwindClasses: string;
}

/**
 * Type-to-style mapping configuration
 * Contains all styling information for each event type
 */
export const TYPE_CONFIG: Record<EventTypeExtended, TypeStyleConfig> = {
  dynamo: {
    label: 'Dynamo',
    cssClass: 'hmc-type-dynamo',
    tailwindClasses: 'bg-orange-500/20 text-orange-400',
  },
  dash: {
    label: 'Dash',
    cssClass: 'hmc-type-dash',
    tailwindClasses: 'bg-sky-500/20 text-sky-400',
  },
  rockets: {
    label: 'Rockets',
    cssClass: 'hmc-type-rockets',
    tailwindClasses: 'bg-red-500/20 text-red-400',
  },
  concert: {
    label: 'Concert',
    cssClass: 'hmc-type-concert',
    tailwindClasses: 'bg-purple-500/20 text-purple-400',
  },
  comedy: {
    label: 'Comedy',
    cssClass: 'hmc-type-comedy',
    tailwindClasses: 'bg-yellow-500/20 text-yellow-400',
  },
  performance: {
    label: 'Performance',
    cssClass: 'hmc-type-performance',
    tailwindClasses: 'bg-pink-500/20 text-pink-400',
  },
  general: {
    label: 'General',
    cssClass: 'hmc-type-general',
    tailwindClasses: 'bg-gray-500/20 text-gray-400',
  },
  other: {
    label: 'Other',
    cssClass: 'hmc-type-other',
    tailwindClasses: 'bg-slate-500/20 text-slate-400',
  },
};

/**
 * Sports event types for filter categorization
 */
const SPORTS_TYPES: EventTypeExtended[] = ['dynamo', 'dash', 'rockets'];

/**
 * Extended event types that may be stored in database
 * (Currently DB stores: dynamo, dash, general - but UI handles all)
 */
const EXPLICIT_TYPES: EventTypeExtended[] = [
  'dynamo',
  'dash',
  'rockets',
  'concert',
  'comedy',
  'performance',
  'other',
];

/**
 * Build result object from type
 * @param type - The event type
 * @returns Complete EventTypeResult with styling
 */
function buildResult(type: EventTypeExtended): EventTypeResult {
  const config = TYPE_CONFIG[type];
  return {
    type,
    label: config.label,
    cssClass: config.cssClass,
    tailwindClasses: config.tailwindClasses,
  };
}

/**
 * Detect event type from text content
 * Used as fallback when event_type is 'general'
 *
 * @param eventText - Combined lowercase text from event fields
 * @param musicGenre - Music genre if set
 * @returns Detected event type
 */
function detectFromText(eventText: string, musicGenre: MusicGenre | null): EventTypeExtended {
  // Sports teams
  if (eventText.includes('dynamo')) {
    return 'dynamo';
  }
  if (eventText.includes('dash')) {
    return 'dash';
  }
  if (eventText.includes('rockets') || eventText.includes('houston rockets')) {
    return 'rockets';
  }

  // Concert: has music_genre set (and not 'other')
  if (musicGenre && musicGenre !== 'other') {
    return 'concert';
  }

  // Comedy
  if (
    eventText.includes('comedy') ||
    eventText.includes('comedian') ||
    eventText.includes('stand-up') ||
    eventText.includes('standup')
  ) {
    return 'comedy';
  }

  // Performance (theater, ballet, etc.)
  if (
    eventText.includes('theater') ||
    eventText.includes('theatre') ||
    eventText.includes('ballet') ||
    eventText.includes('broadway') ||
    eventText.includes('dance') ||
    eventText.includes('musical') ||
    eventText.includes('opera') ||
    eventText.includes('cirque')
  ) {
    return 'performance';
  }

  // Default fallback
  return 'general';
}

/**
 * Detect event type using 2-tier priority system
 *
 * Priority 1: Use explicit event_type field if set to specific type (not 'general')
 * Priority 2: For 'general' events, detect from text content
 *
 * @param event - The HMC event to analyze
 * @returns EventTypeResult with type, label, and styling classes
 *
 * @example
 * ```typescript
 * const result = detectEventType(event);
 * // result.type = 'concert'
 * // result.label = 'Concert'
 * // result.cssClass = 'hmc-type-concert'
 * // result.tailwindClasses = 'bg-purple-500/20 text-purple-400'
 * ```
 */
export function detectEventType(event: HMCEvent): EventTypeResult {
  // Cast to string to handle both current DB types (dynamo/dash/general)
  // and future extended types that may be stored
  const eventType = event.event_type as string;

  // PRIORITY 1: Use explicit event_type if it's a specific type (not 'general')
  // Check against all known explicit types
  if (EXPLICIT_TYPES.includes(eventType as EventTypeExtended)) {
    return buildResult(eventType as EventTypeExtended);
  }

  // PRIORITY 2: For 'general' type (or any unrecognized type), detect from text content
  if (eventType === 'general' || !eventType) {
    const eventText = `${event.opponent || ''} ${event.event_description || ''} ${event.venue || ''} ${event.special_event || ''}`.toLowerCase();
    const detectedType = detectFromText(eventText, event.music_genre);
    return buildResult(detectedType);
  }

  // Fallback for any unknown type
  return buildResult('other');
}

/**
 * Get simplified filter category for event filtering
 *
 * Categories:
 * - sports: dynamo, dash, rockets
 * - concerts: concert type OR has music_genre set (not 'other')
 * - others: everything else (comedy, performance, general, other)
 *
 * @param event - The HMC event to categorize
 * @returns Filter category: 'concerts', 'sports', or 'others'
 *
 * @example
 * ```typescript
 * const category = getEventFilterCategory(event);
 * if (category === 'sports') {
 *   // Show in sports filter
 * }
 * ```
 */
export function getEventFilterCategory(event: HMCEvent): EventFilterCategory {
  const result = detectEventType(event);

  // Sports: dynamo, dash, rockets
  if (SPORTS_TYPES.includes(result.type)) {
    return 'sports';
  }

  // Concerts: explicit concert type OR has music_genre
  if (result.type === 'concert') {
    return 'concerts';
  }

  // Additional check: if music_genre is set (not 'other'), treat as concert
  if (event.music_genre && event.music_genre !== 'other') {
    return 'concerts';
  }

  // Everything else
  return 'others';
}

/**
 * Check if an event is a sports event
 * @param event - The HMC event to check
 * @returns true if sports event (dynamo, dash, or rockets)
 */
export function isSportsEvent(event: HMCEvent): boolean {
  return getEventFilterCategory(event) === 'sports';
}

/**
 * Check if an event is a concert
 * @param event - The HMC event to check
 * @returns true if concert event
 */
export function isConcertEvent(event: HMCEvent): boolean {
  return getEventFilterCategory(event) === 'concerts';
}

/**
 * Get all event types that match a filter category
 * Useful for building filter queries
 *
 * @param category - The filter category
 * @returns Array of EventTypeExtended that belong to this category
 */
export function getTypesForCategory(category: EventFilterCategory): EventTypeExtended[] {
  switch (category) {
    case 'sports':
      return ['dynamo', 'dash', 'rockets'];
    case 'concerts':
      return ['concert'];
    case 'others':
      return ['comedy', 'performance', 'general', 'other'];
  }
}

/**
 * Get display name for a filter category
 * @param category - The filter category
 * @returns Human-readable category name
 */
export function getCategoryDisplayName(category: EventFilterCategory): string {
  switch (category) {
    case 'sports':
      return 'Sports';
    case 'concerts':
      return 'Concerts';
    case 'others':
      return 'Other Events';
  }
}
